// src/pages/home/Events.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import PageSectionsRenderer from "../../components/common/PageSectionsRenderer";
import VolunteerRegistrationModal from "../../components/VolunteerRegistrationModal";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";
import { errorAlert } from "../../utils/alert";
import {
  getVolunteerRegistrationLabel,
  isPlayerRegistrationOpen,
  isVolunteerRegistrationOpen,
} from "../../utils/eventVolunteer";
import { FaCalendarAlt, FaMapMarkerAlt, FaTimes } from "react-icons/fa";

// --- Helper Functions ---
const formatDate = (raw) => {
  if (!raw) return "Date TBD";
  const d = new Date(raw);
  return Number.isNaN(d.getTime())
    ? raw
    : d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
};

const formatEventDateRange = (startRaw, endRaw) => {
  const start = startRaw || null;
  const end = endRaw || startRaw || null;
  if (!start && !end) return "Date TBD";
  if (!start) return formatDate(end);
  if (!end || end === start) return formatDate(start);
  return `${formatDate(start)} to ${formatDate(end)}`;
};

const parseDateForSort = (ev) => {
  const val = ev?.date || ev?.event_date || ev?.event_end_date || ev?.end_date || null;
  if (!val) return new Date(0);
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? new Date(0) : d;
};

// --- Main Component ---
const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [volunteerEvent, setVolunteerEvent] = useState(null);
  const [closedCollapsed, setClosedCollapsed] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await API.fetchEvents();
        const eventList = res && Array.isArray(res.data) ? res.data : [];
        setEvents(eventList);
      } catch (err) {
        console.error("Error loading events:", err);
        setError("Failed to load events. Please try again later.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!error) return;
    errorAlert("Failed to load events", error);
  }, [error]);

  const { openEvents, volunteerOnlyEvents, closedEvents } = useMemo(() => {
    const arr = Array.isArray(events) ? [...events] : [];
    const open = arr.filter((e) => isPlayerRegistrationOpen(e.registration));
    const volunteerOnly = arr.filter(
      (e) => !isPlayerRegistrationOpen(e.registration) && isVolunteerRegistrationOpen(e)
    );
    const closed = arr.filter(
      (e) => !isPlayerRegistrationOpen(e.registration) && !isVolunteerRegistrationOpen(e)
    );
    
    // Sort by most recent date first
    open.sort((a, b) => parseDateForSort(b) - parseDateForSort(a));
    volunteerOnly.sort((a, b) => parseDateForSort(b) - parseDateForSort(a));
    closed.sort((a, b) => parseDateForSort(b) - parseDateForSort(a));

    return { openEvents: open, volunteerOnlyEvents: volunteerOnly, closedEvents: closed };
  }, [events]);


  const openVolunteerModal = (event) => {
    setVolunteerEvent(event);
  };

  const renderEventCard = (event) => {
    const playerRegistrationOpen = isPlayerRegistrationOpen(event.registration);
    const volunteerRegistrationOpen = isVolunteerRegistrationOpen(event);
    const isClosed = !playerRegistrationOpen && !volunteerRegistrationOpen;
    const eventDate = event.date || event.event_date || null;
    const eventEndDate = event.event_end_date || eventDate;

    const hasImage = event.image && 
                     event.image !== "/images/image-fallback.svg" && 
                     !event.image.toLowerCase().includes("fallback") && 
                     !event.image.toLowerCase().includes("placeholder");
    const imgSrc = resolveAssetUrl(event.image, { folder: "events" });

    return (
      <article
        key={event.id}
        className={`bg-white rounded-xl shadow-lg overflow-hidden flex flex-col group transition-all duration-300 ${
          isClosed ? "opacity-70 grayscale" : "hover:shadow-2xl hover:-translate-y-2"
        }`}
      >
        {hasImage && (
          <div className="relative h-56 w-full">
            <img
              src={imgSrc}
              alt={event.title || "Event image"}
              onError={(e) => {
                e.currentTarget.style.display = "none";
                if (e.currentTarget.parentElement) {
                  e.currentTarget.parentElement.style.display = "none";
                }
              }}
              className="h-full w-full object-cover"
              loading="lazy"
            />
            <div
              className={`absolute top-4 left-4 px-3 py-1 text-xs font-bold rounded-full ${
                playerRegistrationOpen ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-800"
              }`}
            >
              Registration {playerRegistrationOpen ? "Open" : "Closed"}
            </div>
            {!playerRegistrationOpen && volunteerRegistrationOpen ? (
              <div className="absolute top-4 right-4 px-3 py-1 text-xs font-bold rounded-full bg-emerald-100 text-emerald-800">
                {getVolunteerRegistrationLabel(event)}
              </div>
            ) : null}
          </div>
        )}

        <div className="p-6 flex flex-col flex-grow">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="text-xl font-bold text-slate-900">{event.title}</h2>
            {!hasImage && (
              <div className="flex gap-2">
                <span className={`px-2.5 py-0.5 text-xs font-bold rounded-full border ${playerRegistrationOpen ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-50 text-gray-600 border-gray-200"}`}>
                  Reg: {playerRegistrationOpen ? "Open" : "Closed"}
                </span>
                {!playerRegistrationOpen && volunteerRegistrationOpen ? (
                  <span className="px-2.5 py-0.5 text-xs font-bold rounded-full bg-emerald-50 text-emerald-700 border-emerald-200">
                    {getVolunteerRegistrationLabel(event)}
                  </span>
                ) : null}
              </div>
            )}
          </div>
          
          <div className="flex items-center text-sm text-slate-500 mb-4 gap-4">
            <div className="flex items-center gap-2"><FaCalendarAlt /> {formatEventDateRange(eventDate, eventEndDate)}</div>
            <div className="flex items-center gap-2"><FaMapMarkerAlt /> {event.location || "TBD"}</div>
          </div>

          <p className="text-slate-600 flex-grow line-clamp-3">
            {event.description || "No description provided."}
          </p>

          {!playerRegistrationOpen && volunteerRegistrationOpen ? (
            <p className="mt-4 text-sm font-medium text-emerald-700">
              Volunteer / escort registration is still available for this ongoing event.
            </p>
          ) : null}

          <div className="mt-6 flex gap-4">
            <button
              onClick={() => setSelectedEvent(event)}
              className="font-semibold text-white bg-blue-600 rounded-lg px-5 py-2 hover:bg-blue-700 shadow-sm hover:shadow-md transition-all duration-300"
              aria-label={`View details for ${event.title}`}
            >
              View Details
            </button>
            {playerRegistrationOpen && (
              <>
                <button
                  onClick={() => navigate("/register")}
                  className="font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                >
                  Register
                </button>
              </>
            )}
            {volunteerRegistrationOpen ? (
              <button
                onClick={() => openVolunteerModal(event)}
                className="font-semibold text-emerald-700 hover:text-emerald-800 transition-colors"
              >
                Volunteer / Escort
              </button>
            ) : null}
          </div>
        </div>
      </article>
    );
  };
  
  return (
    <>
      <Navbar />

      <main className="bg-slate-50">
        {/* ===== HERO SECTION ===== */}
        <section className="bg-white py-16 text-center border-b border-slate-200">
          <div className="max-w-screen-lg mx-auto px-6">
            <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900">Our Events</h1>
            <p className="text-lg text-slate-600 mt-3 max-w-2xl mx-auto">
              Explore upcoming championships, camps, and community gatherings.
            </p>
          </div>
        </section>

        {/* ===== EVENTS LISTING ===== */}
        <section className="py-20">
          <div className="max-w-screen-xl mx-auto px-6">
            {loading && <div className="text-center text-slate-500">Loading events...</div>}
            {error && <div className="text-center text-red-500">{error}</div>}
            {!loading && !error && (
              <div className="space-y-16">
                {/* Open Events */}
                <div>
                  <h2 className="text-3xl font-bold text-slate-800 mb-6">Open for Registration ({openEvents.length})</h2>
                  {openEvents.length > 0 ? (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">{openEvents.map(renderEventCard)}</div>
                  ) : (
                    <div className="text-center bg-white p-12 rounded-xl shadow-md text-slate-500">No events are currently open for registration.</div>
                  )}
                </div>

                <div>
                  <h2 className="text-3xl font-bold text-slate-800 mb-6">Volunteer / Escort Open Now ({volunteerOnlyEvents.length})</h2>
                  {volunteerOnlyEvents.length > 0 ? (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                      {volunteerOnlyEvents.map(renderEventCard)}
                    </div>
                  ) : (
                    <div className="text-center bg-white p-12 rounded-xl shadow-md text-slate-500">
                      No ongoing events currently need separate volunteer / escort registration.
                    </div>
                  )}
                </div>

                {/* Closed Events */}
                <div>
                  <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6 gap-4">
                    <h2 className="text-3xl font-bold text-slate-800">Past / Closed Events ({closedEvents.length})</h2>
                    <button
                      onClick={() => setClosedCollapsed((p) => !p)}
                      className="font-semibold text-sm text-blue-600 border-2 border-blue-200 rounded-lg px-4 py-2 hover:bg-blue-50 transition-all self-start sm:self-center"
                      aria-expanded={!closedCollapsed}
                    >
                      {closedCollapsed ? "Show Past Events" : "Hide Past Events"}
                    </button>
                  </div>
                  {!closedCollapsed && (
                    closedEvents.length > 0 ? (
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">{closedEvents.map(renderEventCard)}</div>
                    ) : (
                      <div className="text-center bg-white p-12 rounded-xl shadow-md text-slate-500">No past events to show.</div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        </section>

        <PageSectionsRenderer pageKey="events" />
      </main>

      <Footer />

      {/* ===== EVENT DETAILS MODAL ===== */}
      {selectedEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSelectedEvent(null)} />
          <div className="relative max-w-2xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-2xl font-bold text-slate-900">{selectedEvent.title}</h3>
                <button onClick={() => setSelectedEvent(null)} className="text-slate-500 hover:text-slate-800 transition-colors">
                  <FaTimes size={22} />
                </button>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-slate-600 mb-4">
                  <div className="flex items-center gap-2"><FaCalendarAlt /> <strong>Date:</strong> {formatEventDateRange(selectedEvent.date || selectedEvent.event_date, selectedEvent.event_end_date)}</div>
                  <div className="flex items-center gap-2"><FaMapMarkerAlt /> <strong>Location:</strong> {selectedEvent.location || "TBD"}</div>
              </div>

              <p className="text-slate-600 mb-6">{selectedEvent.description || "No description provided."}</p>

              <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between"><strong>Registration:</strong> <span className={isPlayerRegistrationOpen(selectedEvent.registration) ? 'text-green-700 font-semibold' : 'text-slate-500'}>{selectedEvent.registration}</span></div>
                <div className="flex justify-between"><strong>Volunteer / Escort:</strong> <span className={isVolunteerRegistrationOpen(selectedEvent) ? 'text-emerald-700 font-semibold' : 'text-slate-500'}>{getVolunteerRegistrationLabel(selectedEvent)}</span></div>
                <div className="flex justify-between"><strong>Registration Fee:</strong> <span>₹{selectedEvent.registration_fee ?? 0}</span></div>
                <div className="flex justify-between"><strong>Escort Fee:</strong> <span>₹{selectedEvent.escort_fee ?? 0}</span></div>
              </div>

              <div className="mt-6 flex gap-4">
                {isPlayerRegistrationOpen(selectedEvent.registration) && (
                  <>
                    <button
                      onClick={() => { setSelectedEvent(null); navigate("/register"); }}
                      className="font-semibold text-white bg-blue-600 rounded-lg px-5 py-2 hover:bg-blue-700 shadow-sm hover:shadow-md transition-all duration-300"
                    >
                      Register Now
                    </button>
                  </>
                )}
                {isVolunteerRegistrationOpen(selectedEvent) ? (
                  <button
                    onClick={() => {
                      const eventRef = selectedEvent;
                      setSelectedEvent(null);
                      openVolunteerModal(eventRef);
                    }}
                    className="font-semibold text-emerald-700 bg-emerald-100 rounded-lg px-5 py-2 hover:bg-emerald-200 transition-all"
                  >
                    Volunteer / Escort
                  </button>
                ) : null}
                 <button onClick={() => setSelectedEvent(null)} className="font-semibold text-slate-700 bg-slate-200 rounded-lg px-5 py-2 hover:bg-slate-300 transition-all">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <VolunteerRegistrationModal
        event={volunteerEvent}
        onClose={() => setVolunteerEvent(null)}
      />
    </>
  );
};

export default Events;
